/**
 * The fraction wall: rows of bars, each cut into a different number of equal
 * pieces, stacked so the same width can be read off every row at once.
 *
 * It is the one picture that makes equivalence obvious rather than stated. You
 * do not tell a child that two quarters is a half; you shade two quarters, and
 * the edge lands exactly where the half ends, and they see it.
 */

export interface Fraction {
  numerator: number;
  denominator: number;
}

/**
 * The denominators on the wall. Sevenths, ninths and elevenths are left out on
 * purpose: nothing else on the wall lines up with them, so they add a row that
 * can never show the thing the wall exists to show.
 */
export const WALL_ROWS = [1, 2, 3, 4, 5, 6, 8, 10, 12];

export function valueOf(f: Fraction): number {
  return f.denominator === 0 ? 0 : f.numerator / f.denominator;
}

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

export function simplify(f: Fraction): Fraction {
  const d = gcd(Math.abs(f.numerator), Math.abs(f.denominator)) || 1;
  return { numerator: f.numerator / d, denominator: f.denominator / d };
}

/**
 * Every other row of the wall that lands in exactly the same place — the rows
 * whose edge lines up. Exact integer arithmetic only: a wall that highlighted
 * a row because of floating-point slack would be teaching a falsehood.
 */
export function equivalents(f: Fraction, rows: number[] = WALL_ROWS): Fraction[] {
  const out: Fraction[] = [];
  if (f.numerator <= 0 || f.denominator <= 0) return out;
  for (const denominator of rows) {
    if (denominator === f.denominator) continue;
    const numerator = (f.numerator * denominator) / f.denominator;
    if (Number.isInteger(numerator) && numerator > 0 && numerator <= denominator) {
      out.push({ numerator, denominator });
    }
  }
  return out;
}

export function compare(a: Fraction, b: Fraction): number {
  // Cross-multiplied, so 3/5 against 2/3 is decided exactly rather than by
  // two divisions that might land a hair apart.
  const left = a.numerator * b.denominator;
  const right = b.numerator * a.denominator;
  return left === right ? 0 : left < right ? -1 : 1;
}

export function fractionText(f: Fraction): string {
  return `${f.numerator}/${f.denominator}`;
}

const NAMES: Record<string, string> = {
  '1/1': 'the whole thing',
  '1/2': 'a half',
  '1/3': 'a third',
  '2/3': 'two thirds',
  '1/4': 'a quarter',
  '3/4': 'three quarters',
  '1/5': 'a fifth',
  '1/6': 'a sixth',
  '1/8': 'an eighth',
  '1/10': 'a tenth',
  '1/12': 'a twelfth',
};

/** What it is called, if it has a name a child would use. */
export function fractionName(f: Fraction): string | null {
  return NAMES[fractionText(simplify(f))] ?? null;
}

/**
 * How much higher this piece sings than the whole bar.
 *
 * Halve a string and it sounds an octave up; this is that same fact, and the
 * reason a half, a third and a quarter sound like an octave, a twelfth and two
 * octaves rather than like nothing in particular. The wall is a musical
 * instrument that happens to be made of fractions.
 */
export function pitchRatio(f: Fraction): number {
  const value = valueOf(f);
  return value > 0 ? 1 / value : 1;
}
